<script setup>
import moment from "moment-timezone";
import { VDataTableServer } from "vuetify/labs/VDataTable";

moment.tz.setDefault("Asia/Jakarta");

const loading = ref();
const loadingTable = ref();
const loadingClear = ref();

definePage({
  meta: {
    layout: "default",
  },
});
const pageTitle = "Permission & Role";

const breadCrumbs = [
  {
    title: "Dashboard",
    to: "/admin/dashboard",
  },
  {
    title: "System",
    to: null,
  },
  {
    title: pageTitle,
    to: null,
  },
];
const headers = [
  {
    title: "Role Name",
    key: "name",
  },
  {
    title: "Player Type",
    key: "type",
  },
  {
    title: "Role Description",
    key: "description",
  },
  {
    title: "Actions",
    sortable: false,
    key: "actions",
  },
];

const route = useRoute();
const router = useRouter();

const refForm = ref();

// Data table options
const itemsPerPage = ref(10);
const page = ref(1);
const orderBy = ref("createdAt");
const orderDirection = ref("ASC");

const search = ref(null);
const type = ref(null);

const { data: dataQueries, execute: fetchQueries } = await useApi(
  createUrl("/cms/roles", {
    query: {
      limit: itemsPerPage,
      search,
      type,
      page,
      orderBy,
      orderDirection,
    },
  })
);

const updateOptions = async (options) => {
  loadingTable.value = true;
  page.value = options.page;
  orderBy.value = options.sortBy[0]?.key;
  orderDirection.value = options.sortBy[0]?.order;
  fetchQueries().then((value) => {
    loadingTable.value = false;
  });
};

const perPages = [5, 10, 25, 50];

const refreshResult = () => {
  loadingClear.value = true;
  loadingTable.value = true;
  search.value = null;
  type.value = null;
  fetchQueries().then((value) => {
    loadingClear.value = false;
    loadingTable.value = false;
  });
};

const searchResult = () => {
  loading.value = true;
  loadingTable.value = true;
  refForm.value?.validate().then(({ valid: isValid }) => {
    if (isValid) {
      fetchQueries().then((value) => {
        loading.value = false;
        loadingTable.value = false;
      });
    }
  });
};

const dataItems = computed(() => (dataQueries ? dataQueries.value?.data : []));
const totalItems = computed(() =>
  dataQueries ? dataQueries.value?.metadata.pagination.itemCount : []
);

/*
 * Delete
 * */
const valid = ref("");
const errors = ref("");
const deleteDialog = ref(false);
const deleteDialogDisplay = ref(false);
const deleteAlert = ref(false);
const deleteItem = (e, name) => {
  deleteDialog.value = e;
  deleteDialogDisplay.value = name;
};
const deleteOk = async (e) => {
  loadingTable.value = true;
  try {
    const res = await $api("/cms/roles/" + e, {
      method: "DELETE",
      onResponseError({ response }) {
        errors.value = response._data.message;

        setTimeout(() => (errors.value = false), 10000);
      },
    });

    if (res.statusCode === 200) {
      deleteAlert.value = res.message;
    } else if (res.statusCode === 409) {
      deleteAlert.value = res.message;
    }
  } catch (err) {
    deleteAlert.value = `The record count not be deleted, because of an association`;
    errors.value = err.message;
  }
  fetchQueries().then((value) => {
    loadingTable.value = false;
    deleteAlert.value = false;
    errors.value = null;
    deleteDialog.value = false;
    deleteDialogDisplay.value = "";
  });
};
const deleteCancel = () => {
  deleteDialog.value = false;
  deleteDialogDisplay.value = "";
};
</script>

<template>
  <VBreadcrumbs :items="breadCrumbs" class="px-0 pt-0 pb-5" />
  <h4 class="text-h4 my-4">Data {{ pageTitle }}</h4>
  <VCard>
    <VDataTableServer
      v-model:items-per-page="itemsPerPage"
      v-model:page="page"
      :headers="headers"
      :items="dataItems"
      :items-length="totalItems"
      :loading="loadingTable"
      class="text-no-wrap"
      @update:options="updateOptions"
    >
      <template #top>
        <VRow class="ms-4 me-4 my-2 justify-center justify-sm-space-between">
          <VCol cols="24" lg="6" sm="6">
            <VForm
              ref="refForm"
              @submit.prevent="
                () => {
                  searchResult();
                }
              "
            >
              <div class="d-flex flex-rows gap-4">
                <AppTextField
                  v-model="search"
                  clearable
                  placeholder="Input Role Name"
                />
                <AppTextField
                  v-model="type"
                  clearable
                  placeholder="Input Player Type"
                />
                <VBtn
                  :disabled="loading"
                  :loading="loading"
                  color="primary"
                  type="submit"
                >
                  Search

                  <template #loader>
                    <span class="custom-loader">
                      <VIcon class="text-white" icon="tabler-refresh" />
                    </span>
                  </template>
                </VBtn>
                <VBtn
                  :disabled="loadingClear"
                  :loading="loadingClear"
                  color="secondary"
                  @click="refreshResult()"
                >
                  <VIcon icon="tabler-refresh" start />
                  Clear
                </VBtn>
              </div>
            </VForm>
          </VCol>
          <VCol cols="12" lg="3" sm="3">
            <AppSelect
              v-model="itemsPerPage"
              :items="perPages"
              class="w-50 ml-auto"
              placeholder="Per Page"
            />
          </VCol>
          <VCol cols="12" lg="3" sm="3">
            <VBtn :to="{ path: 'role/create' }" block color="primary">
              <VIcon icon="tabler-plus" start />
              Add {{ pageTitle }}
            </VBtn>
          </VCol>
        </VRow>
      </template>
      <template #item.id="{ item }">{{ increment++ }}</template>
      <template #item.actions="{ item }">
        <div class="d-flex gap-1">
          <IconBtn :to="{ path: 'role/view/' + item.id }">
            <VIcon icon="tabler-edit" />
          </IconBtn>
          <IconBtn @click="deleteItem(item.id, item.name)">
            <VIcon icon="tabler-trash" />
          </IconBtn>
        </div>
      </template>
      <template #bottom>
        <VCardText class="pt-2">
          <div
            class="d-flex flex-wrap justify-center justify-sm-space-between gap-y-2 mt-2"
          >
            <div></div>
            <VPagination
              v-model="page"
              :length="Math.ceil(totalItems / itemsPerPage)"
              :total-visible="$vuetify.display.smAndDown ? 3 : 5"
            />
          </div>
        </VCardText>
      </template>
    </VDataTableServer>
  </VCard>
  <!-- 👉 Delete Dialog  -->
  <VDialog v-model="deleteDialog" max-width="500px">
    <VCard v-show="!deleteAlert">
      <VCardTitle class="text-center my-3">
        <p>Are you sure you want to delete this item?</p>
        <h3>{{ deleteDialogDisplay }}</h3>
      </VCardTitle>

      <VCardActions>
        <VSpacer />
        <VBtn color="secondary" variant="outlined" @click="deleteCancel">
          Cancel
        </VBtn>
        <VBtn color="error" variant="elevated" @click="deleteOk(deleteDialog)">
          OK
        </VBtn>
        <VSpacer />
      </VCardActions>
    </VCard>
    <VCard v-show="deleteAlert">
      <VCardText>
        <VAlert type="error">
          {{ deleteAlert }}
        </VAlert>
      </VCardText>
    </VCard>
  </VDialog>
</template>
